// src/Product/Accounting/Reconcilliation.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./Reconcilliation.css";

const MIRROR_PREFIX = "Mirror — ";
const MIRROR_PATTERN = `${MIRROR_PREFIX}%`;

function normalizeTxnType(t) {
  const x = String(t || "")
    .trim()
    .toLowerCase();
  if (x === "credit" || x === "cr") return "Credit";
  if (x === "debit" || x === "dr") return "Debit";
  return "";
}

function safeNum(v) {
  const n = Number(
    String(v ?? "")
      .replace(/,/g, "")
      .trim()
  );
  return Number.isFinite(n) ? n : 0;
}

function money(v) {
  return `₦${safeNum(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString();
}

export default function Reconciliation() {
  const { companyId, companyRole } = useCompany();

  const [loading, setLoading] = useState(false);
  const [pageErr, setPageErr] = useState("");

  const [banks, setBanks] = useState([]);
  const [pastRecs, setPastRecs] = useState([]);

  const [selectedBank, setSelectedBank] = useState("");
  const [statementDate, setStatementDate] = useState("");
  const [statementBal, setStatementBal] = useState("");
  const [periodStartDate, setPeriodStartDate] = useState("");
  const [openingBal, setOpeningBal] = useState(0);

  const [recId, setRecId] = useState(null);
  const [txns, setTxns] = useState([]);
  const [clearedIds, setClearedIds] = useState(new Set());

  const statementBalNum = useMemo(() => safeNum(statementBal), [statementBal]);

  const selectedBankName =
    banks.find((b) => b.id === selectedBank)?.name || "Selected bank";

  const resetActiveReconciliation = useCallback(() => {
    setRecId(null);
    setStatementDate("");
    setStatementBal("");
    setTxns([]);
    setClearedIds(new Set());
  }, []);

  const refreshPastRecs = useCallback(async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from("reconciliations")
      .select(
        `
        id,
        bank_account_id,
        statement_date,
        statement_balance,
        opening_balance,
        system_balance,
        reconciled,
        created_at,
        updated_at,
        created_by,
        user:created_by(first_name,last_name)
      `
      )
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (!error) setPastRecs(data || []);
  }, [companyId]);

  const summary = useMemo(() => {
    const inflowTxns = txns.filter(
      (t) => normalizeTxnType(t.type) === "Credit"
    );
    const outflowTxns = txns.filter(
      (t) => normalizeTxnType(t.type) === "Debit"
    );

    const totalInflow = inflowTxns.reduce((s, t) => s + safeNum(t.amount), 0);
    const totalOutflow = outflowTxns.reduce((s, t) => s + safeNum(t.amount), 0);

    const clearedTxns = txns.filter((t) => clearedIds.has(t.id));
    const unclearedTxns = txns.filter((t) => !clearedIds.has(t.id));

    const clearedInflow = clearedTxns
      .filter((t) => normalizeTxnType(t.type) === "Credit")
      .reduce((s, t) => s + safeNum(t.amount), 0);

    const clearedOutflow = clearedTxns
      .filter((t) => normalizeTxnType(t.type) === "Debit")
      .reduce((s, t) => s + safeNum(t.amount), 0);

    const unclearedInflow = unclearedTxns
      .filter((t) => normalizeTxnType(t.type) === "Credit")
      .reduce((s, t) => s + safeNum(t.amount), 0);

    const unclearedOutflow = unclearedTxns
      .filter((t) => normalizeTxnType(t.type) === "Debit")
      .reduce((s, t) => s + safeNum(t.amount), 0);

    const systemBalance = safeNum(openingBal) + totalInflow - totalOutflow;
    const clearedMovement = clearedInflow - clearedOutflow;
    const adjustedBookBalance = safeNum(openingBal) + clearedMovement;

    const difference = statementBalNum - adjustedBookBalance;

    return {
      totalInflow,
      totalOutflow,
      inflowCount: inflowTxns.length,
      outflowCount: outflowTxns.length,
      systemBalance,
      clearedCount: clearedTxns.length,
      unclearedCount: unclearedTxns.length,
      clearedInflow,
      clearedOutflow,
      unclearedInflow,
      unclearedOutflow,
      adjustedBookBalance,
      difference,
    };
  }, [txns, clearedIds, openingBal, statementBalNum]);

  const allSelected =
    txns.length > 0 && txns.every((t) => clearedIds.has(t.id));
  const canFinalize = recId && Math.abs(summary.difference) < 0.005;

  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        setLoading(true);
        setPageErr("");

        const [banksRes, pastRes] = await Promise.all([
          supabase
            .from("chart_of_accounts")
            .select("id,name")
            .eq("company_id", companyId)
            .eq("category", "Bank"),
          supabase
            .from("reconciliations")
            .select(
              `
              id,
              bank_account_id,
              statement_date,
              statement_balance,
              opening_balance,
              system_balance,
              reconciled,
              created_at,
              updated_at,
              created_by,
              user:created_by(first_name,last_name)
            `
            )
            .eq("company_id", companyId)
            .order("created_at", { ascending: false }),
        ]);

        if (banksRes.error) throw banksRes.error;
        if (pastRes.error) throw pastRes.error;

        setBanks(banksRes.data || []);
        setPastRecs(pastRes.data || []);
      } catch (e) {
        console.error(e);
        setPageErr("We could not load your bank reconciliation data.");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !selectedBank) return;

    (async () => {
      try {
        setLoading(true);
        setPageErr("");

        const { data: lastDone, error: lastDoneErr } = await supabase
          .from("reconciliations")
          .select("statement_date, statement_balance")
          .eq("company_id", companyId)
          .eq("bank_account_id", selectedBank)
          .eq("reconciled", true)
          .order("statement_date", { ascending: false })
          .maybeSingle();

        if (lastDoneErr && lastDoneErr.code !== "PGRST116") {
          throw lastDoneErr;
        }

        const lastStatementDate = lastDone?.statement_date || "";
        const opening = safeNum(lastDone?.statement_balance);

        setPeriodStartDate(lastStatementDate);
        setOpeningBal(opening);

        const { data: openRec, error: recErr } = await supabase
          .from("reconciliations")
          .select("*")
          .eq("company_id", companyId)
          .eq("bank_account_id", selectedBank)
          .eq("reconciled", false)
          .maybeSingle();

        if (recErr && recErr.code !== "PGRST116") throw recErr;

        if (!openRec) {
          resetActiveReconciliation();
          return;
        }

        setRecId(openRec.id);
        setStatementDate(openRec.statement_date || "");
        setStatementBal(
          openRec.statement_balance === null ||
            openRec.statement_balance === undefined
            ? ""
            : String(openRec.statement_balance)
        );

        let txnQuery = supabase
          .from("bank_transactions")
          .select("id,date,description,amount,type,status,bank_account_id")
          .eq("company_id", companyId)
          .eq("bank_account_id", selectedBank)
          .lte("date", openRec.statement_date)
          .neq("status", "Deleted")
          .not("description", "ilike", MIRROR_PATTERN);

        if (lastStatementDate)
          txnQuery = txnQuery.gt("date", lastStatementDate);

        const { data: txnList, error: txnErr } = await txnQuery.order("date", {
          ascending: true,
        });

        if (txnErr) throw txnErr;

        const { data: clearedRows, error: clearedErr } = await supabase
          .from("cleared_transactions")
          .select("transaction_id")
          .eq("reconciliation_id", openRec.id);

        if (clearedErr) throw clearedErr;

        setTxns(txnList || []);
        setClearedIds(
          new Set((clearedRows || []).map((c) => c.transaction_id))
        );
      } catch (e) {
        console.error(e);
        setPageErr("We could not load this bank account reconciliation.");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, selectedBank, resetActiveReconciliation]);

  const startReconciliation = async () => {
    if (!selectedBank || !statementDate || String(statementBal).trim() === "") {
      alert(
        "Please select bank account, statement date, and closing statement balance."
      );
      return;
    }

    try {
      setLoading(true);
      setPageErr("");

      const { data: lastDone, error: lastDoneErr } = await supabase
        .from("reconciliations")
        .select("statement_date, statement_balance")
        .eq("company_id", companyId)
        .eq("bank_account_id", selectedBank)
        .eq("reconciled", true)
        .order("statement_date", { ascending: false })
        .maybeSingle();

      if (lastDoneErr && lastDoneErr.code !== "PGRST116") throw lastDoneErr;

      const lastStatementDate = lastDone?.statement_date || "";
      const opening = safeNum(lastDone?.statement_balance);

      setOpeningBal(opening);
      setPeriodStartDate(lastStatementDate);

      let txnQuery = supabase
        .from("bank_transactions")
        .select("id,date,description,amount,type,status,bank_account_id")
        .eq("company_id", companyId)
        .eq("bank_account_id", selectedBank)
        .lte("date", statementDate)
        .neq("status", "Deleted")
        .not("description", "ilike", MIRROR_PATTERN);

      if (lastStatementDate) txnQuery = txnQuery.gt("date", lastStatementDate);

      const { data: txnList, error: txnErr } = await txnQuery.order("date", {
        ascending: true,
      });

      if (txnErr) throw txnErr;

      const list = txnList || [];
      const totalInflow = list
        .filter((t) => normalizeTxnType(t.type) === "Credit")
        .reduce((s, t) => s + safeNum(t.amount), 0);
      const totalOutflow = list
        .filter((t) => normalizeTxnType(t.type) === "Debit")
        .reduce((s, t) => s + safeNum(t.amount), 0);

      const systemBalance = opening + totalInflow - totalOutflow;

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data: recData, error: recErr } = await supabase
        .from("reconciliations")
        .insert({
          company_id: companyId,
          bank_account_id: selectedBank,
          statement_date: statementDate,
          statement_balance: statementBalNum,
          opening_balance: opening,
          system_balance: systemBalance,
          created_by: user?.id || null,
          reconciled: false,
        })
        .select("id")
        .single();

      if (recErr) throw recErr;

      setRecId(recData.id);
      setTxns(list);
      setClearedIds(new Set());
    } catch (e) {
      console.error(e);
      setPageErr("We could not start the reconciliation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleCleared = async (t) => {
    if (!recId) return;

    try {
      setLoading(true);
      setPageErr("");

      const next = new Set(clearedIds);

      if (next.has(t.id)) {
        const { error } = await supabase
          .from("cleared_transactions")
          .delete()
          .eq("reconciliation_id", recId)
          .eq("transaction_id", t.id);

        if (error) throw error;
        next.delete(t.id);
      } else {
        const { error } = await supabase
          .from("cleared_transactions")
          .upsert(
            { reconciliation_id: recId, transaction_id: t.id },
            { onConflict: "reconciliation_id,transaction_id" }
          );

        if (error) throw error;
        next.add(t.id);
      }

      setClearedIds(next);
    } catch (e) {
      console.error(e);
      setPageErr("We could not update the cleared status.");
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = async () => {
    if (!recId || !txns.length) return;

    try {
      setLoading(true);
      setPageErr("");

      if (allSelected) {
        const { error } = await supabase
          .from("cleared_transactions")
          .delete()
          .eq("reconciliation_id", recId);

        if (error) throw error;
        setClearedIds(new Set());
      } else {
        const inserts = txns.map((t) => ({
          reconciliation_id: recId,
          transaction_id: t.id,
        }));

        const { error } = await supabase
          .from("cleared_transactions")
          .upsert(inserts, {
            onConflict: "reconciliation_id,transaction_id",
          });

        if (error) throw error;
        setClearedIds(new Set(txns.map((t) => t.id)));
      }
    } catch (e) {
      console.error(e);
      setPageErr("We could not update all transactions.");
    } finally {
      setLoading(false);
    }
  };

  const softDeleteTxn = async (t) => {
    if (
      !window.confirm(
        "Remove this transaction from reconciliation? This will mark it as Deleted, not permanently erase it."
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setPageErr("");

      if (recId) {
        await supabase
          .from("cleared_transactions")
          .delete()
          .eq("reconciliation_id", recId)
          .eq("transaction_id", t.id);
      }

      const { error } = await supabase
        .from("bank_transactions")
        .update({ status: "Deleted" })
        .eq("id", t.id)
        .eq("company_id", companyId);

      if (error) throw error;

      setTxns((prev) => prev.filter((x) => x.id !== t.id));
      setClearedIds((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
    } catch (e) {
      console.error(e);
      setPageErr("We could not remove this transaction.");
    } finally {
      setLoading(false);
    }
  };

  const restart = async () => {
    if (!recId) {
      resetActiveReconciliation();
      return;
    }

    if (
      !window.confirm(
        "Restart this reconciliation? Your ticked items will be cleared, but your bank transactions will remain."
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setPageErr("");

      const { error: clearedErr } = await supabase
        .from("cleared_transactions")
        .delete()
        .eq("reconciliation_id", recId);

      if (clearedErr) throw clearedErr;

      const { error: recErr } = await supabase
        .from("reconciliations")
        .delete()
        .eq("id", recId)
        .eq("company_id", companyId)
        .eq("reconciled", false);

      if (recErr) throw recErr;

      resetActiveReconciliation();
      await refreshPastRecs();
    } catch (e) {
      console.error(e);
      setPageErr("We could not restart the reconciliation.");
    } finally {
      setLoading(false);
    }
  };

  const finalize = async () => {
    if (!canFinalize) return;

    try {
      setLoading(true);
      setPageErr("");

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { error } = await supabase
        .from("reconciliations")
        .update({
          reconciled: true,
          system_balance: summary.systemBalance,
          updated_at: new Date().toISOString(),
          completed_by: user?.id || null,
        })
        .eq("id", recId)
        .eq("company_id", companyId);

      if (error) throw error;

      alert("Reconciliation completed successfully.");
      await refreshPastRecs();
    } catch (e) {
      console.error(e);
      setPageErr("We could not finalize this reconciliation.");
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (r) => {
    try {
      const { data: items, error } = await supabase
        .from("cleared_transactions")
        .select(`transaction:bank_transactions(date,description,amount,type)`)
        .eq("reconciliation_id", r.id);

      if (error) throw error;

      const doc = new jsPDF();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Bank Reconciliation Report", 14, 15);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Statement Date: ${fmtDate(r.statement_date)}`, 14, 24);
      doc.text(`Statement Balance: ${money(r.statement_balance)}`, 14, 30);
      doc.text(`Opening Balance: ${money(r.opening_balance)}`, 14, 36);
      doc.text(`System Balance: ${money(r.system_balance)}`, 14, 42);

      doc.autoTable({
        startY: 50,
        head: [["Date", "Description", "Inflow", "Outflow"]],
        body: (items || []).map((c) => {
          const tp = normalizeTxnType(c.transaction?.type);
          const amt = safeNum(c.transaction?.amount);

          return [
            c.transaction?.date || "",
            c.transaction?.description || "",
            tp === "Credit" ? money(amt) : "",
            tp === "Debit" ? money(amt) : "",
          ];
        }),
      });

      doc.save(`reconciliation_${r.statement_date}.pdf`);
    } catch (e) {
      console.error(e);
      alert("Failed to export report.");
    }
  };

  if (companyRole !== "admin") {
    return (
      <div className="rec-container">
        <div className="rec-card">
          <p className="rec-error">
            Access denied. Only admins can perform bank reconciliation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rec-container">
      <div className="rec-header">
        <div>
          <h2 className="rec-title">Bank Reconciliation</h2>
          <p className="rec-sub">
            Tick the transactions that appear on your bank statement. When the
            difference becomes zero, you can finalize.
          </p>
        </div>
      </div>

      <div className="rec-card rec-guide">
        <h3>Simple guide</h3>
        <div className="guide-grid">
          <div>
            <strong>Opening Balance</strong>
            <p>
              This is the closing balance from your last completed
              reconciliation for this same bank account.
            </p>
          </div>
          <div>
            <strong>Statement Date</strong>
            <p>
              This is the end date printed on the bank statement you are
              reconciling, not today’s date unless today is the statement end
              date.
            </p>
          </div>
          <div>
            <strong>Closing Balance</strong>
            <p>
              This is the final balance shown on the bank statement as at the
              statement date.
            </p>
          </div>
        </div>
      </div>

      <div className="rec-card rec-form">
        <div className="form-row">
          <div className="input-group">
            <label>Bank Account</label>
            <select
              disabled={!!recId || loading}
              value={selectedBank}
              onChange={(e) => setSelectedBank(e.target.value)}
            >
              <option value="">Select bank account</option>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label>Opening Balance</label>
            <input value={money(openingBal)} disabled />
            <small>Auto-filled from last completed reconciliation.</small>
          </div>

          <div className="input-group">
            <label>Statement Date</label>
            <input
              type="date"
              disabled={!!recId || loading}
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
            />
            <small>Use the bank statement end date.</small>
          </div>

          <div className="input-group">
            <label>Closing Statement Balance</label>
            <input
              type="number"
              disabled={!!recId || loading}
              value={statementBal}
              onChange={(e) => setStatementBal(e.target.value)}
              placeholder="0.00"
            />
            <small>Use the final balance on the bank statement.</small>
          </div>

          <div className="input-group action-group">
            {!recId ? (
              <button
                className="rec-primary"
                onClick={startReconciliation}
                disabled={loading}
              >
                Start Reconciliation
              </button>
            ) : (
              <button
                className="rec-secondary"
                onClick={restart}
                disabled={loading}
              >
                Restart
              </button>
            )}
          </div>
        </div>

        <p className="rec-period">
          Period:{" "}
          <strong>
            {periodStartDate ? fmtDate(periodStartDate) : "Beginning"}
          </strong>{" "}
          to{" "}
          <strong>
            {statementDate ? fmtDate(statementDate) : "statement date"}
          </strong>
        </p>

        {pageErr && <p className="rec-error">{pageErr}</p>}
      </div>

      {recId && (
        <>
          <div className="rec-card rec-summary">
            <div className="summary-grid">
              <div>
                <span>Opening Balance</span>
                <strong>{money(openingBal)}</strong>
              </div>
              <div>
                <span>Total Inflows</span>
                <strong>{money(summary.totalInflow)}</strong>
                <small>{summary.inflowCount} transaction(s)</small>
              </div>
              <div>
                <span>Total Outflows</span>
                <strong className="negative">
                  {money(summary.totalOutflow)}
                </strong>
                <small>{summary.outflowCount} transaction(s)</small>
              </div>
              <div>
                <span>Book/System Balance</span>
                <strong>{money(summary.systemBalance)}</strong>
              </div>
              <div>
                <span>Cleared Items</span>
                <strong>{summary.clearedCount}</strong>
                <small>
                  {money(summary.adjustedBookBalance)} adjusted balance
                </small>
              </div>
              <div>
                <span>Difference</span>
                <strong
                  className={
                    Math.abs(summary.difference) > 0.005
                      ? "negative"
                      : "success"
                  }
                >
                  {money(summary.difference)}
                </strong>
                <small>
                  {canFinalize ? "Ready to finalize" : "Must be zero"}
                </small>
              </div>
            </div>

            <div className="rec-explainer">
              <p>
                <strong>Accounting check:</strong> Closing statement balance
                should equal Opening Balance plus cleared inflows less cleared
                outflows.
              </p>
              <p>
                Uncleared items are not wrong. They are transactions in KostPadi
                that have not yet appeared on the bank statement.
              </p>
            </div>

            <div className="summary-actions">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={loading}
                />
                <span>Tick all transactions as cleared</span>
              </label>

              <button
                className="rec-primary"
                onClick={finalize}
                disabled={!canFinalize || loading}
                title={
                  !canFinalize
                    ? "Difference must be zero before finalizing."
                    : "Finalize reconciliation"
                }
              >
                Finalize Reconciliation
              </button>
            </div>
          </div>

          <div className="rec-card">
            <div className="table-title">
              <div>
                <h3>{selectedBankName} Statement Matching</h3>
                <p>Tick only the items you can see on the bank statement.</p>
              </div>
              <div className="mini-stat">
                Uncleared: <strong>{summary.unclearedCount}</strong>
              </div>
            </div>

            {txns.length === 0 ? (
              <div className="empty">
                No transactions found for this statement period.
              </div>
            ) : (
              <div className="table-wrap">
                <table className="rec-table">
                  <thead>
                    <tr>
                      <th>Cleared?</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Inflow</th>
                      <th>Outflow</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t) => {
                      const isCleared = clearedIds.has(t.id);
                      const tp = normalizeTxnType(t.type);
                      const amt = safeNum(t.amount);

                      return (
                        <tr key={t.id} className={isCleared ? "cleared" : ""}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isCleared}
                              onChange={() => toggleCleared(t)}
                              disabled={loading}
                            />
                          </td>
                          <td>{fmtDate(t.date)}</td>
                          <td>{t.description || "No description"}</td>
                          <td>{tp === "Credit" ? money(amt) : "—"}</td>
                          <td className="negative">
                            {tp === "Debit" ? money(amt) : "—"}
                          </td>
                          <td>
                            <span
                              className={
                                isCleared
                                  ? "pill success-pill"
                                  : "pill warning-pill"
                              }
                            >
                              {isCleared ? "On statement" : "Not ticked"}
                            </span>
                          </td>
                          <td>
                            <button
                              className="link danger"
                              onClick={() => softDeleteTxn(t)}
                              disabled={loading}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <div className="rec-card rec-log">
        <h3>Past Reconciliations</h3>

        <div className="table-wrap">
          <table className="rec-table">
            <thead>
              <tr>
                <th>Statement Date</th>
                <th>Bank</th>
                <th>Opening Balance</th>
                <th>Closing Balance</th>
                <th>Prepared By</th>
                <th>Status</th>
                <th>Report</th>
              </tr>
            </thead>
            <tbody>
              {pastRecs.length ? (
                pastRecs.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.statement_date)}</td>
                    <td>
                      {banks.find((b) => b.id === r.bank_account_id)?.name ||
                        r.bank_account_id}
                    </td>
                    <td>{money(r.opening_balance)}</td>
                    <td>{money(r.statement_balance)}</td>
                    <td>
                      {`${r.user?.first_name || ""} ${
                        r.user?.last_name || ""
                      }`.trim() || "—"}
                    </td>
                    <td>
                      <span
                        className={
                          r.reconciled
                            ? "pill success-pill"
                            : "pill warning-pill"
                        }
                      >
                        {r.reconciled ? "Complete" : "In Progress"}
                      </span>
                    </td>
                    <td>
                      <button
                        className="link"
                        onClick={() => downloadReport(r)}
                        disabled={!r.reconciled}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="empty">
                    No reconciliation history yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {loading && <div className="rec-loading">Working…</div>}
    </div>
  );
}
